import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Info,
  Loader2,
  Maximize2,
  Pause,
  Play,
  RotateCcw,
} from 'lucide-react';
import {
  fetchPublicVirtualTour,
} from '../../lib/forfeoReserveVirtualTourClient';
import type {
  ForfeoReserveVirtualTourHotspot,
  ForfeoReserveVirtualTourScene,
  ForfeoReserveVirtualTourSnapshot,
} from '../../../supabase/functions/_shared/forfeo-reserve-virtual-tour';

type Props = {
  businessId: string;
  businessName: string;
};

export default function ReserveVirtualTourViewer({
  businessId,
  businessName,
}: Props) {
  const [tour, setTour] = useState<ForfeoReserveVirtualTourSnapshot | null>(null);
  const [sceneId, setSceneId] = useState('');
  const [panX, setPanX] = useState(50);
  const [autoRotate, setAutoRotate] = useState(false);
  const [busy, setBusy] = useState(true);
  const [info, setInfo] = useState('');
  const drag = useRef<{ pointerId: number; startX: number; startPan: number } | null>(null);
  const viewport = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    setBusy(true);
    fetchPublicVirtualTour(businessId)
      .then((snapshot) => {
        if (!active) return;
        setTour(snapshot);
        setSceneId(snapshot?.startSceneId || snapshot?.scenes[0]?.id || '');
      })
      .catch(() => {
        if (active) setTour(null);
      })
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [businessId]);

  useEffect(() => {
    if (!autoRotate) return;
    const timer = window.setInterval(() => {
      setPanX((value) => (value + 0.2) % 100);
    }, 40);
    return () => window.clearInterval(timer);
  }, [autoRotate]);

  const scene = useMemo(
    () => tour?.scenes.find((entry) => entry.id === sceneId) || tour?.scenes[0],
    [tour, sceneId],
  );

  useEffect(() => {
    if (scene) setPanX(Math.max(0, Math.min(100, 50 + scene.initialYaw / 3.6)));
  }, [scene?.id]);

  if (busy) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-[2rem] border border-slate-200 bg-white">
        <Loader2 className="animate-spin text-violet-600" />
      </div>
    );
  }

  if (!tour || !scene) return null;

  const currentIndex = Math.max(
    0,
    tour.scenes.findIndex((entry) => entry.id === scene.id),
  );

  function changeScene(nextId: string) {
    if (tour?.scenes.some((entry) => entry.id === nextId)) {
      setInfo('');
      setSceneId(nextId);
    }
  }

  function step(direction: -1 | 1) {
    if (!tour) return;
    const next = (currentIndex + direction + tour.scenes.length) % tour.scenes.length;
    changeScene(tour.scenes[next].id);
  }

  function activateHotspot(hotspot: ForfeoReserveVirtualTourHotspot) {
    if (hotspot.kind === 'scene' && hotspot.targetSceneId) {
      changeScene(hotspot.targetSceneId);
      return;
    }
    if (hotspot.kind === 'reserve') {
      const url = new URL(`/booking/${businessId}`, window.location.origin);
      url.searchParams.set('businessId', businessId);
      if (hotspot.catalogItemId) {
        url.searchParams.set('catalogItemId', hotspot.catalogItemId);
      }
      window.location.assign(url.toString());
      return;
    }
    if (hotspot.kind === 'external' && hotspot.externalUrl) {
      window.open(hotspot.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setInfo(hotspot.informationText || hotspot.label);
  }

  function renderMedia(sceneEntry: ForfeoReserveVirtualTourScene) {
    if (sceneEntry.media.kind === 'video') {
      return (
        <video
          className="h-full w-full object-cover"
          controls
          playsInline
          poster={sceneEntry.media.posterUrl}
          aria-label={sceneEntry.media.altText}
        >
          <source src={sceneEntry.media.signedUrl} type={sceneEntry.media.mimeType} />
        </video>
      );
    }

    if (sceneEntry.media.kind === 'standard_image') {
      return (
        <img
          src={sceneEntry.media.signedUrl}
          alt={sceneEntry.media.altText}
          className="h-full w-full object-cover"
          draggable={false}
        />
      );
    }

    return (
      <div
        className="h-full w-full cursor-grab bg-cover bg-center active:cursor-grabbing"
        role="img"
        aria-label={sceneEntry.media.altText}
        style={{
          backgroundImage: `url("${sceneEntry.media.signedUrl}")`,
          backgroundPosition: `${panX}% center`,
          backgroundSize: 'auto 100%',
          backgroundRepeat: 'repeat-x',
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startPan: panX,
          };
          setAutoRotate(false);
        }}
        onPointerMove={(event) => {
          if (!drag.current || drag.current.pointerId !== event.pointerId) return;
          const width = Math.max(320, event.currentTarget.clientWidth);
          const delta = ((event.clientX - drag.current.startX) / width) * 100;
          setPanX((drag.current.startPan - delta + 100) % 100);
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
      />
    );
  }

  return (
    <section className="overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-2xl">
      <div className="flex flex-col gap-3 border-b border-white/10 bg-white px-5 py-4 text-slate-900 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-violet-300">
            Visite virtuelle
          </p>
          <h2 className="text-xl font-black">{tour.title || businessName}</h2>
          {scene.description && (
            <p className="mt-1 max-w-2xl text-sm text-slate-700">{scene.description}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {scene.media.kind === 'panorama_image' && (
            <>
              <button
                type="button"
                onClick={() => setAutoRotate((value) => !value)}
                className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-xs font-bold hover:bg-white/20"
              >
                {autoRotate ? <Pause size={16} /> : <Play size={16} />}
                {autoRotate ? 'Pause' : 'Rotation'}
              </button>
              <button
                type="button"
                onClick={() => setPanX(50)}
                className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-xs font-bold hover:bg-white/20"
              >
                <RotateCcw size={16} /> Recentrer
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => viewport.current?.requestFullscreen?.()}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-xs font-bold hover:bg-white/20"
          >
            <Maximize2 size={16} /> Plein écran
          </button>
        </div>
      </div>

      <div
        ref={viewport}
        className="relative h-[360px] overflow-hidden bg-white sm:h-[500px] lg:h-[620px]"
      >
        {renderMedia(scene)}

        {scene.hotspots.map((hotspot) => (
          <button
            key={hotspot.id}
            type="button"
            onClick={() => activateHotspot(hotspot)}
            className="absolute z-10 inline-flex min-h-10 max-w-[180px] -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border border-white/60 bg-white/95 px-3 py-2 text-left text-xs font-black text-slate-900 shadow-xl backdrop-blur hover:bg-violet-700"
            style={{
              left: `${hotspot.xPercent}%`,
              top: `${hotspot.yPercent}%`,
            }}
            aria-label={hotspot.label}
          >
            {hotspot.kind === 'reserve' && <CalendarCheck size={15} />}
            {hotspot.kind === 'external' && <ExternalLink size={15} />}
            {hotspot.kind === 'information' && <Info size={15} />}
            <span className="line-clamp-2">{hotspot.label}</span>
          </button>
        ))}

        {tour.scenes.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/55 p-3 text-white backdrop-blur hover:bg-black/75"
              aria-label="Scène précédente"
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/55 p-3 text-white backdrop-blur hover:bg-black/75"
              aria-label="Scène suivante"
            >
              <ChevronRight />
            </button>
          </>
        )}

        {info && (
          <div className="absolute bottom-4 left-4 right-4 z-20 rounded-2xl border border-white/20 bg-white/90 p-4 text-sm text-slate-900 shadow-2xl backdrop-blur sm:left-auto sm:max-w-md">
            <div className="flex items-start justify-between gap-3">
              <p>{info}</p>
              <button type="button" onClick={() => setInfo('')} aria-label="Fermer">
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-3 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        {tour.scenes.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => changeScene(entry.id)}
            className={`min-h-12 rounded-xl border px-3 text-left text-sm font-bold transition ${
              entry.id === scene.id
                ? 'border-violet-500 bg-violet-50 text-violet-800'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {entry.title}
          </button>
        ))}
      </div>

      <details className="border-t border-slate-200 bg-white p-4 text-sm">
        <summary className="cursor-pointer font-black text-slate-700">
          Version accessible de la visite
        </summary>
        <div className="mt-4 grid gap-4">
          {tour.scenes.map((entry) => (
            <article key={entry.id} className="rounded-xl bg-slate-50 p-4">
              <h3 className="font-black">{entry.title}</h3>
              <p className="mt-1 text-slate-600">
                {entry.description || entry.media.altText}
              </p>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-slate-600">
                {entry.hotspots.map((hotspot) => (
                  <li key={hotspot.id}>{hotspot.label}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}
